import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, Lock } from 'lucide-react';
import { useState } from 'react';
import { AssetDrawer } from '@/components/accountant/asset-drawer';
import { ReasonDialog } from '@/components/accountant/reason-dialog';
import { ErrorBanner, Field, NativeSelect } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  METHOD_LABELS,
  assetStatus,
  lastWholeMonth,
  lifeLabel,
  monthLabel,
  useAssets,
  useDepreciationPreview,
  useDepreciationRuns,
  type DepreciationRun,
} from '@/lib/accountant';
import { api } from '@/lib/api';
import { useMe } from '@/lib/clients';
import { todayIso } from '@/lib/dates';
import { formatAmount, formatDate, formatDateTime } from '@/lib/format';
import { formatMinor, toMinor } from '@/lib/money';
import { useOrgId } from '@/lib/org';
import { homeCurrency } from '@/lib/org-locale';

export const Route = createFileRoute('/accounting/fixed-assets/')({ component: FixedAssets });

/** Screen A12 (document 06): fixed asset register and depreciation runs. */
function FixedAssets() {
  const orgId = useOrgId();
  const me = useMe();
  const navigate = useNavigate();
  const assets = useAssets();
  const isStaff = me.data?.isStaff ?? false;
  const [creating, setCreating] = useState(false);
  const [running, setRunning] = useState(false);
  if (!orgId) return <EmptyState title="Select a client" />;
  const list = assets.data ?? [];
  const total = (k: 'cost' | 'accumulated' | 'netBookValue') =>
    formatMinor(
      list.filter((a) => a.status === 'active').reduce((s, a) => s + (toMinor(a[k]) ?? 0n), 0n),
    );
  return (
    <div>
      <PageHeader
        title="Fixed assets"
        description="The asset register: cost, depreciation and net book value. Depreciation posts monthly through the ledger, and disposals book the gain or loss."
        actions={
          isStaff ? (
            <>
              <Button variant="outline" onClick={() => setRunning(true)} disabled={!list.length}>
                Run depreciation
              </Button>
              <Button onClick={() => setCreating(true)}>New asset</Button>
            </>
          ) : null
        }
      />
      {assets.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : assets.isError ? (
        <EmptyState title="Could not load the register" hint={assets.error.message} />
      ) : !list.length ? (
        <EmptyState
          title="No fixed assets yet"
          hint="Add vehicles, equipment, fit-out and software with their cost and useful life."
          action={
            isStaff ? <Button onClick={() => setCreating(true)}>New asset</Button> : undefined
          }
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Asset no.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Asset account</TableHead>
                <TableHead>In service</TableHead>
                <TableHead>Method</TableHead>
                <TableHead className="text-right">Cost</TableHead>
                <TableHead className="text-right">Accumulated depreciation</TableHead>
                <TableHead className="text-right">Net book value</TableHead>
                <TableHead>Depreciated through</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((a) => (
                <TableRow
                  key={a.id}
                  className="cursor-pointer"
                  data-testid={`asset-${a.number}`}
                  onClick={() =>
                    void navigate({
                      to: '/accounting/fixed-assets/$assetId',
                      params: { assetId: a.id },
                    })
                  }
                >
                  <TableCell>
                    <Link
                      to="/accounting/fixed-assets/$assetId"
                      params={{ assetId: a.id }}
                      className="text-primary hover:underline"
                    >
                      {a.number}
                    </Link>
                  </TableCell>
                  <TableCell className="font-medium">{a.name}</TableCell>
                  <TableCell>{a.assetAccount}</TableCell>
                  <TableCell>{formatDate(a.inServiceDate)}</TableCell>
                  <TableCell>
                    {METHOD_LABELS[a.method]}, {lifeLabel(a.usefulLifeMonths)}
                  </TableCell>
                  <TableCell className="tnum text-right">{formatAmount(a.cost)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(a.accumulated)}</TableCell>
                  <TableCell className="tnum text-right">{formatAmount(a.netBookValue)}</TableCell>
                  <TableCell>
                    {a.depreciatedThrough ? monthLabel(a.depreciatedThrough) : '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant={a.status === 'disposed' ? 'outline' : 'secondary'}>
                      {assetStatus(a)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold">
                <TableCell colSpan={5}>Total in service</TableCell>
                <TableCell className="tnum text-right">{total('cost')}</TableCell>
                <TableCell className="tnum text-right">{total('accumulated')}</TableCell>
                <TableCell className="tnum text-right">{total('netBookValue')}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </div>
      )}
      <Runs />
      <AssetDrawer
        open={creating}
        asset={null}
        onOpenChange={setCreating}
        onSaved={(a) =>
          void navigate({ to: '/accounting/fixed-assets/$assetId', params: { assetId: a.id } })
        }
      />
      <RunDialog open={running} onClose={() => setRunning(false)} />
    </div>
  );
}

function Runs() {
  const orgId = useOrgId();
  const me = useMe();
  const qc = useQueryClient();
  const runs = useDepreciationRuns();
  const [undoing, setUndoing] = useState<DepreciationRun | null>(null);
  const undo = useMutation({
    mutationFn: ({ r, reason }: { r: DepreciationRun; reason: string }) =>
      api<DepreciationRun[]>(`/orgs/${orgId}/depreciation/runs/${r.id}/undo`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: async (_v, { r }) => {
      await qc.invalidateQueries();
      setUndoing(null);
      toast(`Depreciation for ${monthLabel(r.month)} undone.`);
    },
  });
  if (!runs.data?.length) return null;
  return (
    <section className="mt-8">
      <h2 className="mb-2 text-base font-semibold">Depreciation runs</h2>
      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              <TableHead>Run</TableHead>
              <TableHead className="text-right">Assets</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Journal no.</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>When</TableHead>
              <TableHead className="w-20">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.data.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{monthLabel(r.month)}</TableCell>
                <TableCell>
                  {r.trigger === 'schedule'
                    ? 'Scheduled'
                    : r.trigger === 'seed'
                      ? 'Set up'
                      : 'Manual'}
                </TableCell>
                <TableCell className="tnum text-right">{r.assetCount}</TableCell>
                <TableCell className="tnum text-right">{formatAmount(r.total)}</TableCell>
                <TableCell>
                  {r.entryId ? (
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: r.entryId }}
                      className="text-primary hover:underline"
                    >
                      {r.entryNo}
                    </Link>
                  ) : null}
                </TableCell>
                <TableCell>
                  {r.status === 'failed' ? (
                    <span className="flex items-start gap-1 text-xs text-destructive">
                      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden /> {r.error}
                    </span>
                  ) : (
                    <Badge variant={r.status === 'posted' ? 'secondary' : 'outline'}>
                      {r.status === 'posted' ? 'Posted' : 'Undone'}
                    </Badge>
                  )}
                </TableCell>
                <TableCell>
                  {formatDateTime(r.createdAt)}
                  {r.createdBy ? (
                    <span className="text-muted-foreground"> · {r.createdBy}</span>
                  ) : null}
                </TableCell>
                <TableCell>
                  {me.data?.isStaff && r.canUndo ? (
                    <Button variant="link" size="sm" className="px-1" onClick={() => setUndoing(r)}>
                      Undo
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <ReasonDialog
        open={undoing !== null}
        title={`Undo depreciation for ${undoing ? monthLabel(undoing.month) : ''}?`}
        description="The run's entry is reversed on its own date and its months can be charged again. A closed month refuses it."
        confirmLabel="Undo run"
        destructive
        pending={undo.isPending}
        error={undo.error?.message ?? null}
        onConfirm={(reason) => undoing && undo.mutate({ r: undoing, reason })}
        onClose={() => {
          undo.reset();
          setUndoing(null);
        }}
      />
    </section>
  );
}

/** The months a run can go through: the last whole month and the eleven before it. */
function monthOptions(today: string): string[] {
  const out: string[] = [];
  let m = lastWholeMonth(today);
  for (let i = 0; i < 24; i++) {
    out.push(m);
    m = lastWholeMonth(m);
  }
  const current = `${today.slice(0, 7)}-01`;
  return [current, ...out];
}

function RunDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const today = todayIso();
  const [through, setThrough] = useState(lastWholeMonth(today));
  const preview = useDepreciationPreview(open ? through : null);
  const run = useMutation({
    mutationFn: () =>
      api<{
        posted: Array<{ month: string; total: string }>;
        failed: { month: string; error: string } | null;
      }>(`/orgs/${orgId}/depreciation/run`, {
        method: 'POST',
        body: JSON.stringify({ throughMonth: through }),
      }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      if (r.failed) {
        toast(
          `${r.posted.length} month${r.posted.length === 1 ? '' : 's'} posted; ${monthLabel(r.failed.month)} could not post: ${r.failed.error}`,
          { tone: 'error' },
        );
      } else {
        toast(
          `Depreciation posted for ${r.posted.length} month${r.posted.length === 1 ? '' : 's'}.`,
        );
      }
      onClose();
    },
  });
  const months = preview.data?.months ?? [];
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          run.reset();
          onClose();
        }
      }}
    >
      <DialogContent className="sm:max-w-[640px]">
        <DialogHeader>
          <DialogTitle>Run depreciation</DialogTitle>
          <DialogDescription>
            Posts one entry per month, dated its last day, for every asset with depreciation due. A
            month already charged is never charged again; the register also runs this every night.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <ErrorBanner>{run.error?.message ?? null}</ErrorBanner>
          <Field label="Through the end of" className="w-56">
            {(id) => (
              <NativeSelect id={id} value={through} onChange={(e) => setThrough(e.target.value)}>
                {monthOptions(today).map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          {preview.isPending ? (
            <p className="text-sm text-muted-foreground">Working out what is due…</p>
          ) : !months.length ? (
            <p className="text-sm text-muted-foreground">
              Nothing is due through {monthLabel(through)}.
            </p>
          ) : (
            <div className="max-h-72 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <tbody>
                  {months.map((m) => (
                    <tr key={m.month} className="border-b align-top last:border-0">
                      <td className="w-28 px-3 py-2 font-medium">
                        <span className="inline-flex items-center gap-1">
                          {monthLabel(m.month)}
                          {m.locked ? (
                            <span title="This month is closed; the run will stop here">
                              <Lock
                                className="size-3.5 text-destructive"
                                aria-label="Closed month"
                              />
                            </span>
                          ) : null}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {m.lines.map((l) => `${l.number} ${l.name}`).join(', ')}
                      </td>
                      <td className="tnum px-3 py-2 text-right">{formatAmount(m.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {preview.data && months.length ? (
            <p className="tnum text-right text-sm font-semibold">
              Total {homeCurrency()} {formatAmount(preview.data.total)}
            </p>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!months.length || run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Posting…' : 'Post depreciation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
